import type { Language } from "../../contexts/LanguageContext";

export const landingLabels: Record<Language, {
  hero: {
    badge: string;
    title: [string, string];
    cta: string;
    calendarAlt: string;
    homeAlt: string;
  };
  features: {
    heading: [string, string];
    card1: { imgAlt: string; items: string[]; cta: string };
    card2: { imgAlt: string; items: string[]; cta: string };
  };
  pricing: {
    title: string;
    description: string;
    plans: {
      monthly: { name: string; description: string };
      semiannual: { name: string; description: string };
      annual: { name: string; description: string };
    };
    subscribe: string;
  };
  faq: {
    title: string;
    description: string;
    items: { question: string; answer: string }[];
  };
  footer: {
    rights: string;
    privacy: string;
    whatsapp: string;
  };
}> = {
  pt: {
    hero: {
      badge: "Agenda inteligente para estúdios de fisioterapia e pilates.",
      title: ["Centralize pacientes, turmas e equipe", "em um só lugar"],
      cta: "Experimente grátis",
      calendarAlt: "Calendário",
      homeAlt: "Home",
    },
    features: {
      heading: [
        "Descubra como nossas funcionalidades",
        "podem melhorar a sua rotina:",
      ],
      card1: {
        imgAlt: "Evolução do paciente",
        items: [
          "Acompanhe a evolução clínica com fotos, notas e anexos em cada sessão.",
          "Dicte exercícios e protocolos por voz e deixe a IA organizar o texto.",
        ],
        cta: "Criar conta grátis",
      },
      card2: {
        imgAlt: "Profissionais",
        items: [
          "Monte agendas para fisioterapeutas e instrutores de pilates em poucos cliques.",
          "Controle planos, pacotes e recebimentos dos alunos em um só painel.",
        ],
        cta: "Criar conta grátis",
      },
    },
    pricing: {
      title: "Você pode usar de graça!",
      description:
        "Mas se quiser alguma funcionalidade específica, temos um plano com mais recursos para você.",
      plans: {
        monthly: { name: "Mensal", description: "Pagamento mensal" },
        semiannual: { name: "Semestral", description: "5% de desconto" },
        annual: { name: "Anual", description: "10% de desconto" },
      },
      subscribe: "Escolher plano",
    },
    faq: {
      title: "Perguntas Frequentes",
      description:
        "Respostas rápidas para organizar seu estúdio de fisioterapia e pilates com tranquilidade.",
      items: [
        {
          question: "Como o plano gratuito ajuda meu estúdio?",
          answer:
            "Você gerencia até 10 pacientes ativos para testar a plataforma. Quando precisar de mais, é só migrar para um plano pago sem perder dados.",
        },
        {
          question: "Posso cadastrar diferentes profissionais e modalidades?",
          answer:
            "Sim. Cadastre fisioterapeutas, instrutores de pilates e organize agendas individuais ou coletivas sem complicação.",
        },
        {
          question: "Consigo registrar a evolução dos pacientes?",
          answer:
            "Cada atendimento pode receber notas, anexos e observações por voz, mantendo o histórico clínico sempre acessível.",
        },
        {
          question: "Quais são as formas de pagamento?",
          answer:
            "Aceitamos PIX e cartão de crédito. Em breve, teremos mais opções de pagamento.",
        },
      ],
    },
    footer: {
      rights: "Clocker. Todos os direitos reservados.",
      privacy: "Política de Privacidade e Termos de Uso",
      whatsapp: "Falar pelo WhatsApp",
    },
  },
  en: {
    hero: {
      badge: "Smart scheduling for physiotherapy and Pilates studios.",
      title: ["Centralize patients, classes, and equipment", "with an easy-to-use agenda"],
      cta: "Try it for free",
      calendarAlt: "Calendar",
      homeAlt: "Home",
    },
    features: {
      heading: [
        "Built for the pace of your physiotherapy and Pilates studio",
        "Automate bookings, treatment plans, and payments.",
      ],
      card1: {
        imgAlt: "Patient evolution",
        items: [
          "Track clinical evolution with photos, notes, and attachments in every session.",
          "Dictate exercises and protocols by voice and let AI structure the notes.",
        ],
        cta: "Create a free account",
      },
      card2: {
        imgAlt: "Professionals",
        items: [
          "Build calendars for physiotherapists and Pilates instructors in a few clicks.",
          "Manage plans, packages, and student payments from a single dashboard.",
        ],
        cta: "Create a free account",
      },
    },
    pricing: {
      title: "Plans tailored to your studio",
      description:
        "Start for free and unlock advanced features as your studio grows.",
      plans: {
        monthly: { name: "Monthly", description: "Flexibility to organize your team month by month." },
        semiannual: { name: "Semiannual", description: "Smart savings for growing studios." },
        annual: { name: "Annual", description: "10% off for year-round stability." },
      },
      subscribe: "Choose plan",
    },
    faq: {
      title: "Frequently Asked Questions",
      description:
        "Quick answers to keep your physiotherapy and Pilates studio running smoothly.",
      items: [
        {
          question: "How does the free plan support my studio?",
          answer:
            "You can manage up to 10 active patients while testing the platform. When you need more capacity, upgrade without losing any data.",
        },
        {
          question: "Can I register different professionals and modalities?",
          answer:
            "Yes. Add physiotherapists, Pilates instructors, and organize both private and group sessions with ease.",
        },
        {
          question: "Can I document patient progression?",
          answer:
            "Each appointment supports notes, attachments, and voice dictation so clinical history is always up to date.",
        },
        {
          question: "What payment methods are available?",
          answer:
            "We accept PIX and credit card. More payment options coming soon.",
        },
      ],
    },
    footer: {
      rights: "Clocker. All rights reserved.",
      privacy: "Privacy Policy and Terms of Use",
      whatsapp: "Contact via WhatsApp",
    },
  },
};
